import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Login } from '@/pages/auth';
import { Spinner } from '@/ui';
import { useAuth } from './AuthContext.jsx';
import { homeForCapability } from './constants.js';
import './LoginRoute.css';

// /login with a live session goes to the entry of the active capability, as
// after a login (REC-1837). Without a destination (capability with no area of
// its own, no memberships) the form renders as always, so there is no loop.
// While the first session check runs, a spinner renders instead of the form,
// so a live session goes to its entry without flashing the login. Only that
// first check: `login()` also raises `loading`, and an unmount then would drop
// the form state, so once the form is drawn it stays.
export default function LoginRoute() {
  const {
    isAuthenticated,
    loading,
    activeCapability,
    memberships,
    selectOrganization,
  } = useAuth();
  const { search, state } = useLocation();
  const [firstCheck, setFirstCheck] = useState(loading);
  if (firstCheck && !loading) setFirstCheck(false);

  // Someone else's session can be open in a shared browser: the activation and
  // the password reset end here with a notice for the person who just did them,
  // who has to log in, so the form stays whatever session there is.
  const keepForm = Boolean(state?.accountActivated || state?.passwordReset);

  // The receptor portal sends a user whose active org is another one to their
  // receptor org, as the login does. Once selected, the active capability is
  // receptor and the redirect below takes over. With no receptor org in the
  // session the form stays, so the login can show that nothing is available.
  const portalReceptor =
    new URLSearchParams(search).get('portal') === 'receptor';
  const live = isAuthenticated && !loading && !keepForm;
  const receptorOrgId =
    live && portalReceptor && activeCapability !== 'receptor'
      ? memberships?.find((m) => m.capabilities?.[0] === 'receptor')
          ?.organizationId
      : undefined;
  const portalWithoutReceptor =
    live && portalReceptor && activeCapability !== 'receptor' && !receptorOrgId;

  useEffect(() => {
    if (receptorOrgId) selectOrganization(receptorOrgId);
  }, [receptorOrgId]);

  if (receptorOrgId) return null;

  if (firstCheck) {
    return (
      <div className="login-route-checking">
        <Spinner size={64} />
      </div>
    );
  }

  const home =
    live && !portalWithoutReceptor && homeForCapability(activeCapability);
  if (home) return <Navigate to={home} replace />;

  return (
    <div className={portalReceptor ? 'receptor-login' : undefined}>
      <Login />
    </div>
  );
}
